import { useEffect, useMemo, useRef } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { getJeepRallyMediaUrl } from "../../../../../../api/jeep-rally/api";
import {
  isWebpFile,
  MAX_CHECKPOINT_SLOTS,
} from "../utils/checkpointForm.utils";

const WebpImageField = ({ slot, disabled, error, onChange }) => {
  const inputRef = useRef(null);

  const filePreviewUrl = useMemo(() => {
    if (!(slot.imageFile instanceof File)) return "";
    return URL.createObjectURL(slot.imageFile);
  }, [slot.imageFile]);

  useEffect(() => {
    return () => {
      if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    };
  }, [filePreviewUrl]);

  const previewUrl =
    filePreviewUrl || getJeepRallyMediaUrl(slot.mediaUrl) || "";

  const handlePick = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = "";
    if (!file || disabled) return;

    if (!isWebpFile(file)) {
      onChange({
        imageFile: null,
        imagePreview: "",
        localError: "Only .webp images are allowed.",
      });
      return;
    }

    onChange({
      imageFile: file,
      mediaUrl: slot.mediaUrl || "",
      localError: "",
    });
  };

  const clearImage = () => {
    onChange({
      imageFile: null,
      mediaUrl: "",
      localError: "",
    });
  };

  return (
    <Form.Group className="mb-0">
      <Form.Label>Image (webp only)</Form.Label>
      <div className="d-flex gap-2 align-items-start flex-wrap">
        <div
          className="d-flex align-items-center justify-content-center overflow-hidden border rounded"
          style={{ width: 88, height: 64, flexShrink: 0 }}
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={slot.title || "Checkpoint"}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <span className="text-muted small">No image</span>
          )}
        </div>
        <div>
          <input
            ref={inputRef}
            type="file"
            accept=".webp,image/webp"
            className="d-none"
            disabled={disabled}
            onChange={handlePick}
          />
          <Button
            type="button"
            size="sm"
            variant="outline-primary"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          >
            {slot.mediaUrl || slot.imageFile ? "Replace webp" : "Upload webp"}
          </Button>
          {(slot.mediaUrl || slot.imageFile) && (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="px-0 ms-2"
              disabled={disabled}
              onClick={clearImage}
            >
              Clear
            </Button>
          )}
          {(error || slot.localError) && (
            <div className="text-danger small mt-1">
              {error || slot.localError}
            </div>
          )}
          {!error && !slot.localError && (
            <Form.Text className="text-muted d-block">
              Accepts .webp only. Uploaded via /uploads before save.
            </Form.Text>
          )}
        </div>
      </div>
    </Form.Group>
  );
};

const CheckpointSlotEditor = ({
  slot,
  errors = {},
  disabled = false,
  onChange,
  onClear,
}) => {
  return (
    <div className="border rounded p-3 mb-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">Checkpoint {slot.order}</h6>
        <Button
          type="button"
          variant="outline-secondary"
          size="sm"
          disabled={disabled}
          onClick={onClear}
        >
          Clear slot
        </Button>
      </div>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={slot.title}
              onChange={(e) => onChange({ title: e.target.value })}
              placeholder={`Checkpoint ${slot.order}`}
              disabled={disabled}
              isInvalid={!!errors.title}
            />
            <Form.Control.Feedback type="invalid">
              {errors.title}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>KM Start</Form.Label>
            <Form.Control
              type="number"
              step="any"
              value={slot.km_start}
              onChange={(e) => onChange({ km_start: e.target.value })}
              disabled={disabled}
              isInvalid={!!errors.km_start}
            />
            <Form.Control.Feedback type="invalid">
              {errors.km_start}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>KM End</Form.Label>
            <Form.Control
              type="number"
              step="any"
              value={slot.km_end}
              onChange={(e) => onChange({ km_end: e.target.value })}
              disabled={disabled}
              isInvalid={!!errors.km_end}
            />
            <Form.Control.Feedback type="invalid">
              {errors.km_end}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Description points</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={slot.description_points_text}
              onChange={(e) =>
                onChange({ description_points_text: e.target.value })
              }
              placeholder={"One point per line"}
              disabled={disabled}
            />
            <Form.Text className="text-muted">
              Optional. One description point per line.
            </Form.Text>
          </Form.Group>
        </Col>

        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Media</Form.Label>
            <div className="d-flex gap-3 mb-2">
              <Form.Check
                type="radio"
                id={`media-image-${slot.order}`}
                name={`media-type-${slot.order}`}
                label="Image"
                checked={slot.mediaType === "image"}
                disabled={disabled}
                onChange={() =>
                  onChange({
                    mediaType: "image",
                    imageFile: null,
                    localError: "",
                  })
                }
              />
              <Form.Check
                type="radio"
                id={`media-video-${slot.order}`}
                name={`media-type-${slot.order}`}
                label="Video"
                checked={slot.mediaType === "video"}
                disabled={disabled}
                onChange={() =>
                  onChange({
                    mediaType: "video",
                    imageFile: null,
                    localError: "",
                  })
                }
              />
            </div>

            {slot.mediaType === "image" ? (
              <WebpImageField
                slot={slot}
                disabled={disabled}
                error={errors.media}
                onChange={onChange}
              />
            ) : (
              <Form.Group className="mb-0">
                <Form.Label>Video URL</Form.Label>
                <Form.Control
                  type="url"
                  value={slot.mediaUrl}
                  onChange={(e) => onChange({ mediaUrl: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  disabled={disabled}
                  isInvalid={!!errors.media}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.media}
                </Form.Control.Feedback>
              </Form.Group>
            )}
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

const CheckpointFormEditor = ({
  context,
  slots = [],
  slotErrors = {},
  formError = "",
  submitting = false,
  readOnly = false,
  onSlotChange,
  onClearSlot,
  onSubmit,
  onBack,
}) => {
  const disabled = submitting || readOnly;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h5 className="mb-1">Edit Checkpoints</h5>
          <div className="text-muted small">
            Max {MAX_CHECKPOINT_SLOTS} checkpoints for this stage and category.
          </div>
        </div>
        <Button
          type="button"
          variant="outline-secondary"
          size="sm"
          onClick={onBack}
          disabled={submitting}
        >
          Back to categories
        </Button>
      </div>

      <div className="border rounded p-3 mb-4 bg-light">
        <Row>
          <Col md={4}>
            <div className="text-muted small">Stage</div>
            <div className="fw-semibold">{context?.stageLabel || "-"}</div>
          </Col>
          <Col md={4}>
            <div className="text-muted small">Type</div>
            <div className="fw-semibold">{context?.typeLabel || "-"}</div>
          </Col>
          <Col md={4}>
            <div className="text-muted small">Category</div>
            <div className="fw-semibold">{context?.categoryLabel || "-"}</div>
          </Col>
        </Row>
      </div>

      {formError ? (
        <Alert variant="danger" className="mb-3">
          {formError}
        </Alert>
      ) : null}

      {slots.map((slot) => (
        <CheckpointSlotEditor
          key={slot.order}
          slot={slot}
          errors={slotErrors[slot.order] || {}}
          disabled={disabled}
          onChange={(patch) => onSlotChange(slot.order, patch)}
          onClear={() => onClearSlot(slot.order)}
        />
      ))}

      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button
          type="button"
          variant="outline-secondary"
          onClick={onBack}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button type="button" disabled={disabled} onClick={onSubmit}>
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            "Save Checkpoints"
          )}
        </Button>
      </div>
    </div>
  );
};

export default CheckpointFormEditor;
